import React, { useEffect, useState } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { adminApi } from '../../services/adminApi';
import { AdminOrgListItem } from '../../types';
import {
  Building2,
  LogOut,
  Mail,
  Plus,
  CheckCircle2,
  XCircle,
  PauseCircle,
  PlayCircle,
  ExternalLink,
  Users,
  ScrollText,
} from 'lucide-react';

export function AdminPortalLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navItem = (to: string, label: string, icon: React.ReactNode) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] font-semibold transition-all ${
        isActive(to)
          ? 'bg-slate-900 text-white shadow-md'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent'
      }`}
    >
      {icon}
      {label}
    </Link>
  );

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#f6f8f7] font-sans">
      <nav className="w-[280px] bg-white border-r border-slate-200/60 flex flex-col p-6 flex-shrink-0 relative z-20 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <img src="/taplo-mark.png" alt="Taplo" className="w-10 h-10 select-none" draggable={false} />
          <div>
            <div className="text-[13px] font-bold text-slate-900">Platform Admin</div>
            <div className="text-[12px] text-slate-500 truncate">{admin?.email}</div>
          </div>
        </div>

        <div className="h-px bg-slate-100 my-2" />

        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest px-4 mb-4 mt-2">
          Manage
        </div>

        <div className="flex flex-col gap-2 flex-1">
          {navItem('/admin', 'Workspaces', <Building2 className="w-5 h-5" />)}
          {navItem('/admin/invites', 'Invites', <Mail className="w-5 h-5" />)}
          {navItem('/admin/admins', 'Admins', <Users className="w-5 h-5" />)}
          {navItem('/admin/audit-logs', 'Audit Log', <ScrollText className="w-5 h-5" />)}
        </div>

        <div className="pt-4 border-t border-slate-100/80">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-500 font-semibold text-[15px] rounded-2xl hover:bg-red-50 transition-all text-left border border-transparent hover:border-red-100"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </nav>

      <main className="flex-1 overflow-auto h-full">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminPortalDashboard() {
  const [orgs, setOrgs] = useState<AdminOrgListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    orgName: '',
    subdomain: '',
    ownerEmail: '',
    ownerFullName: '',
    ownerPassword: '',
  });
  const [creating, setCreating] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { organizations } = await adminApi.listOrganizations();
      setOrgs(organizations);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      await adminApi.createOrganization({
        orgName: createForm.orgName,
        subdomain: createForm.subdomain.toLowerCase(),
        ownerEmail: createForm.ownerEmail,
        ownerFullName: createForm.ownerFullName,
        ownerPassword: createForm.ownerPassword,
      });
      setShowCreate(false);
      setCreateForm({ orgName: '', subdomain: '', ownerEmail: '', ownerFullName: '', ownerPassword: '' });
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDisable = async (id: string, isDisabled: boolean) => {
    setError('');
    try {
      if (isDisabled) {
        await adminApi.enableOrganization(id);
      } else {
        if (!confirm('Disable this workspace? Members will be unable to access the POS until you re-enable it.')) return;
        await adminApi.disableOrganization(id);
      }
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleImpersonate = async (org: AdminOrgListItem) => {
    setError('');
    setImpersonating(org.id);
    try {
      const { token, subdomain } = await adminApi.impersonateOrganization(org.id);
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;

      let tenantUrl: string;
      if (hostname === 'localhost' || hostname.startsWith('127.')) {
        const port = window.location.port ? `:${window.location.port}` : '';
        tenantUrl = `${protocol}//${hostname}${port}/login?_tok=${token}&subdomain=${subdomain}`;
      } else {
        // Production: taplo.cc → subdomain.taplo.cc
        const rootDomain = hostname.split('.').slice(-2).join('.');
        tenantUrl = `${protocol}//${subdomain}.${rootDomain}/login?_tok=${token}`;
      }

      window.open(tenantUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImpersonating(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto pb-24 p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Workspaces</h1>
          <p className="text-slate-500 font-medium mt-1">{orgs.length} tenant{orgs.length === 1 ? '' : 's'}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-5 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 shadow-md"
        >
          <Plus className="w-5 h-5" />
          New Workspace
        </button>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {showCreate && (
        <div className="bg-white p-8 rounded-[2rem] mb-8 shadow-xl shadow-slate-200/40 border border-slate-200/60">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Create Workspace</h2>
          <form onSubmit={handleCreate} className="flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Workspace Name" required>
                <input className={inputCls} type="text" placeholder="Saltwater POS" value={createForm.orgName} onChange={e => setCreateForm({ ...createForm, orgName: e.target.value })} required />
              </Field>
              <Field label="Subdomain" required>
                <div className="flex items-stretch h-14 relative group">
                  <input
                    type="text"
                    placeholder="saltwater"
                    value={createForm.subdomain}
                    onChange={e => setCreateForm({ ...createForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                    className="flex-1 px-5 bg-white border border-slate-200 border-r-0 rounded-l-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all"
                  />
                  <span className="px-5 flex items-center bg-slate-50 border border-slate-200 border-l-0 rounded-r-2xl text-[15px] font-medium text-slate-400 whitespace-nowrap">
                    .taplo.cc
                  </span>
                </div>
              </Field>
              <Field label="Owner Full Name" required>
                <input className={inputCls} type="text" placeholder="Jane Doe" value={createForm.ownerFullName} onChange={e => setCreateForm({ ...createForm, ownerFullName: e.target.value })} required />
              </Field>
              <Field label="Owner Email" required>
                <input className={inputCls} type="email" placeholder="jane@example.com" value={createForm.ownerEmail} onChange={e => setCreateForm({ ...createForm, ownerEmail: e.target.value })} required />
              </Field>
              <Field label="Owner Password" required>
                <input className={inputCls} type="password" placeholder="Temporary password" value={createForm.ownerPassword} onChange={e => setCreateForm({ ...createForm, ownerPassword: e.target.value })} required minLength={6} />
              </Field>
            </div>
            <div className="flex gap-3 mt-2">
              <button type="submit" disabled={creating} className="px-6 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 disabled:opacity-50">
                {creating ? 'Creating…' : 'Create Workspace'}
              </button>
              <button type="button" onClick={() => { setShowCreate(false); setError(''); }} className="px-6 py-3.5 bg-white text-slate-600 border border-slate-200 rounded-2xl font-bold hover:bg-slate-50 transition-all active:scale-95">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Workspace</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Owner</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Stripe</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => {
                const liveOk = !!org.stripe_live_onboarding_complete;
                const testOk = !!org.stripe_test_onboarding_complete;
                const disabled = !!org.is_disabled;
                return (
                  <tr key={org.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800 text-[15px]">{org.name}</div>
                      <a href={`https://${org.subdomain}.taplo.cc/`} target="_blank" rel="noreferrer" className="text-[13px] font-medium text-[#0f766e] hover:underline">
                        {org.subdomain}.taplo.cc
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-700 text-[14px]">{org.owner_name || '—'}</div>
                      <div className="text-[13px] text-slate-500">{org.owner_email || ''}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 text-[12px] font-bold ${liveOk ? 'text-[#0f766e]' : 'text-slate-400'}`}>
                          {liveOk ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          Live
                        </span>
                        <span className={`inline-flex items-center gap-1 text-[12px] font-bold ${testOk ? 'text-amber-600' : 'text-slate-400'}`}>
                          {testOk ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          Test
                        </span>
                        <span className="text-[11px] uppercase font-bold text-slate-400 tracking-wide mt-0.5">
                          Active: {org.stripe_mode}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {disabled ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200 uppercase tracking-wide">
                          Disabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleImpersonate(org)}
                          disabled={impersonating === org.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-bold bg-white text-slate-600 border border-slate-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-all active:scale-95 disabled:opacity-50"
                          title="Open as owner (1-hour session)"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {impersonating === org.id ? '…' : 'Open'}
                        </button>
                        <button
                          onClick={() => handleDisable(org.id, disabled)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-bold transition-all active:scale-95 ${
                            disabled
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                              : 'bg-white text-slate-500 border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                          }`}
                        >
                          {disabled ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                          {disabled ? 'Enable' : 'Disable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {orgs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-slate-400 font-medium">
                    No workspaces yet. Click <strong>New Workspace</strong> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all placeholder-slate-300';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
        {label}{required ? ' *' : ''}
      </label>
      {children}
    </div>
  );
}
